import { TeammateDetails } from '@/types/teammates';
import { Card, Stack, Typography } from '@mui/material';
import { SVLink } from '../core';
import { useState } from 'react';
import ResetPasswordModal from '../modals/ResetPasswordModal';
import ChangeEmailModal from '../modals/ChangeEmailModal';
import ChangePhoneModal from '../modals/ChangePhoneModal';
import {
  useResetTeammatePassword,
  useStartTeammateUpdate,
  useChangeTeammateEmail,
  useChangeTeammatePhone,
  useReactivateTeammate,
} from '@/hooks/requests/useTeammates';
import { Status } from '@/types/common';
import ToggleTeammateLockModal from './modals/ToggleTeammateLockModal';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { createFullName } from '@/utils/string';
import ReactivateModal from '../modals/ReactivateModal';
import TeammateDeactivateModal from './modals/TeammateDeactivateModal';

type TeammateDetailsActionsProps = {
  teammateDetails: TeammateDetails;
};

export default function TeammateDetailsActions({
  teammateDetails,
}: TeammateDetailsActionsProps) {
  // Data extraction and utilities
  const { uuid, user, company } = teammateDetails;
  const teammateFullName = createFullName(
    teammateDetails.user.firstName,
    teammateDetails.user.lastName,
  );
  const { scrollToTop } = useScrollToTop();

  const [openResetPassword, setOpenResetPassword] = useState(false);
  const [openChangeEmail, setOpenChangeEmail] = useState(false);
  const [openChangePhone, setOpenChangePhone] = useState(false);
  const [openTeammateLockToggle, setOpenTeammateLockToggle] = useState(false);
  const [openDeactivate, setOpenDeactivate] = useState(false);
  const [openReactivate, setOpenReactivate] = useState(false);

  const { resetTeammatePassword, isResetTeammatePasswordPending } =
    useResetTeammatePassword(uuid);
  const { startTeammateUpdate } = useStartTeammateUpdate(uuid);
  const { changeTeammateEmail, isChangeTeammateEmailPending } =
    useChangeTeammateEmail(uuid);
  const { changeTeammatePhone, isChangeTeammatePhonePending } =
    useChangeTeammatePhone(uuid);

  const { reactivateTeammate, isReactivateTeammatePending } =
    useReactivateTeammate(uuid);

  return (
    <Card variant="fullHeight">
      <Typography variant="h5" mb={2}>
        Actions
      </Typography>
      <Stack spacing={2}>
        <SVLink onClick={() => setOpenResetPassword(true)} to="">
          Reset Password
        </SVLink>
        <SVLink onClick={() => setOpenChangeEmail(true)} to="">
          Change Email
        </SVLink>
        <SVLink onClick={() => setOpenChangePhone(true)} to="">
          Change Auth Phone
        </SVLink>
        <SVLink onClick={() => setOpenTeammateLockToggle(true)} to="">
          {user.isLocked ? 'Unlock Teammate' : 'Lock Teammate'}
        </SVLink>
        {teammateDetails.status === Status.ACTIVE ? (
          <SVLink isWarningLink onClick={() => setOpenDeactivate(true)} to="">
            Deactivate Teammate
          </SVLink>
        ) : (
          <SVLink isWarningLink onClick={() => setOpenReactivate(true)} to="">
            Reactivate Teammate
          </SVLink>
        )}
      </Stack>
      <ResetPasswordModal
        userType="Teammate"
        open={openResetPassword}
        setOpen={setOpenResetPassword}
        resetPassword={resetTeammatePassword}
        isResetPasswordLoading={isResetTeammatePasswordPending}
        scrollToTop={scrollToTop}
      />
      <ChangeEmailModal
        userType="Teammate"
        open={openChangeEmail}
        setOpen={setOpenChangeEmail}
        sendOtp={startTeammateUpdate}
        changeEmail={changeTeammateEmail}
        isChangeEmailLoading={isChangeTeammateEmailPending}
        scrollToTop={scrollToTop}
      />
      <ChangePhoneModal
        userType="Teammate"
        open={openChangePhone}
        setOpen={setOpenChangePhone}
        sendOtp={startTeammateUpdate}
        changePhone={changeTeammatePhone}
        isChangePhonePending={isChangeTeammatePhonePending}
        scrollToTop={scrollToTop}
      />
      <ToggleTeammateLockModal
        open={openTeammateLockToggle}
        setOpen={setOpenTeammateLockToggle}
        teammate={{
          uuid: teammateDetails.uuid,
          fullName: teammateFullName,
          isLocked: user.isLocked,
        }}
        scrollToTop={scrollToTop}
      />
      <TeammateDeactivateModal
        id={uuid}
        companyId={company.uuid}
        open={openDeactivate}
        setOpen={setOpenDeactivate}
      />
      <ReactivateModal
        userType="Teammate"
        open={openReactivate}
        setOpen={setOpenReactivate}
        reactivate={reactivateTeammate}
        isReactivatePending={isReactivateTeammatePending}
        scrollToTop={scrollToTop}
      />
    </Card>
  );
}
